/**
 * `RealtimeSession.ts` — port of Unity's `RealtimeSession.cs` protocol
 * state machine (trimmed to the web slice).
 *
 * Owns the OpenAI Realtime event protocol over an injected transport:
 * `session.update` (instructions + tools + audio + server VAD),
 * conversation items, response lifecycle, function-call dispatch to the
 * tool executor, and usage parsing. Session hosting/reconnect policy lives
 * in `AIRealtimeSessionModel`; UI state lives behind `AIInterfaceModel`.
 */

import { randomUuid } from "@models/utils/uuid";
import { Emitter } from "@core/events/emitter";
import { getAiConfig } from "@ai/aiConfig";
import type {
  IRealtimeTransport,
  RealtimeMicrophoneReason,
  RealtimeTransportKind,
} from "./IRealtimeTransport";
import type { AgentToolExecutorModel } from "@ai/executor/AgentToolExecutorModel";
import type { IAIAgentToolsModel } from "@ai/registry/AIAgentToolRegistryModel";
import { toolHandedOverSpokenTurn } from "@ai/executor/toolTurnHandover";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";

export interface RealtimeSessionOptions {
  readonly instructions: string;
  readonly voiceName: string;
  readonly enableAudioOutput: boolean;
  readonly enableInputAudioTranscription: boolean;
  /**
   * ISO-639-1 code pinned for INPUT TRANSCRIPTION, or "" to let the recognizer
   * pick. Unpinned, `gpt-4o-transcribe` CODE-SWITCHES: "how high is the front
   * cover" came back as "How high ist Frontkopf" (user-reported 2026-09-06) —
   * answered correctly in English, but the user reads the transcript.
   */
  readonly transcriptionLanguage: string;
  /**
   * Whether server VAD auto-creates a response when the user stops speaking.
   * FALSE while the Responses text bridge answers the active role's turns:
   * the session keeps transcribing (the transcript routes to the bridge) and
   * keeps speaking `INTERNAL SAY:` lines on explicit `response.create`, but
   * must not answer the user itself — that would be two agents replying.
   */
  readonly autoRespondToUserAudio: boolean;
}

/**
 * How the session CONNECTS, resolved from the ACTIVE role by the policy
 * provider (the account's `roleModels` config plus the account-wide
 * `webRtcRealtimeTransport` dev setting; shipped default: `workflow-builder` →
 * gpt-5.6-sol at medium effort, everything else the stock realtime model on
 * the WebSocket).
 *
 * Two of the three members are fixed for a connection's LIFE and one is not,
 * which is the whole reason this type exists as a unit:
 *
 *   - `model` rides the connection URL, so a change RECONNECTS.
 *   - `transport` decides which class is constructed, so a change RECONNECTS.
 *   - `reasoningEffort` rides `session.update`, so a change is just a context
 *     refresh.
 *
 * `AIOrchestratorModel.applySessionModelPolicy` is what applies that split.
 */
export interface RealtimeSessionConnectionConfig {
  readonly model: string;
  /** `null` = omit the `reasoning` key entirely. */
  readonly reasoningEffort: string | null;
  /** Which `IRealtimeTransport` implementation to construct. */
  readonly transport: RealtimeTransportKind;
}

export interface IRealtimeSessionPolicyProvider {
  buildSessionOptions(): RealtimeSessionOptions;
  /** Per-turn "User Context:" message, or null (Unity `BuildTurnContext`). */
  buildTurnUserContext(): string | null;
  /**
   * Model, reasoning effort and transport for the active role — see
   * {@link RealtimeSessionConnectionConfig}.
   *
   * ⚠ **Must stay a pure synchronous read** (map lookups only: no async, no
   * memoisation, no side effects). It runs far more often than it looks —
   * `buildSessionPayloadFrom` calls it on every `sendSessionUpdate` AND every
   * `refreshSessionContext` (once per user turn), and the debug panel's
   * Context tab re-renders it off a 1 s revision tick. An impure version would
   * let the construct-time call and the payload-time call disagree about the
   * transport, so the session host would build one class while the panel
   * reported the other.
   */
  resolveSessionConnection(): RealtimeSessionConnectionConfig;
}

/**
 * How long to wait for `session.created` before sending the session config
 * regardless. Comfortably past the ~450 ms the campfire proxy takes to dial
 * upstream, and short enough that a proxy which never sends the event costs
 * one turn's worth of delay rather than the session's whole context.
 */
const K_SESSION_READY_FALLBACK_MS = 3000;

/** Server VAD constants (Unity `BuildSessionUpdate`). */
const SERVER_VAD = {
  threshold: 0.5,
  prefix_padding_ms: 300,
  silence_duration_ms: 500,
} as const;

/**
 * Manual Listen right after Stop can make the server VAD transcribe a short
 * echo/noise tail as a user turn. Keep this window intentionally narrow: normal
 * one-word commands outside an interrupted-output boundary must still work.
 */
const K_POST_OUTPUT_MANUAL_LISTEN_GUARD_MS = 7000;
const K_GUARDED_INPUT_ARTIFACT_MAX_MS = 1075;

interface PendingFunctionCall {
  callId: string;
  name: string;
  argsJson: string;
}

interface PendingVoiceInput {
  readonly guarded: boolean;
  startAudioMs: number | null;
  endAudioMs: number | null;
}

export class RealtimeSession {
  readonly onStatusChanged = new Emitter<string>();
  readonly onResponseDelta = new Emitter<string>();
  readonly onAssistantTextCompleted = new Emitter<string>();
  readonly onThinkingStateChanged = new Emitter<boolean>();
  /** Response lifecycle (`response.created` → true, `response.done` → false). */
  readonly onActiveResponseChanged = new Emitter<boolean>();
  readonly onUserVoiceStarted = new Emitter<void>();
  readonly onUserVoiceStopped = new Emitter<void>();
  readonly onUserTranscriptDelta = new Emitter<string>();
  readonly onUserTranscriptCompleted = new Emitter<string>();
  readonly onToolExecutionStarted = new Emitter<{ toolName: string }>();
  readonly onToolExecutionCompleted = new Emitter<{ toolName: string; result: string }>();
  readonly onUsage = new Emitter<{
    model: string;
    inputTokens: number;
    outputTokens: number;
    inputAudioTokens: number;
    outputAudioTokens: number;
    cachedInputTokens: number;
    source: "Realtime" | "Input Transcription";
  }>();
  readonly onError = new Emitter<string>();
  /**
   * Whether the session can carry an outgoing user item RIGHT NOW — see
   * {@link isReadyForUserInput}. Emitted on the transition only.
   */
  readonly onReadyForUserInputChanged = new Emitter<boolean>();

  readonly sessionId = `web-${randomUuid().slice(0, 8)}`;

  private readonly m_transport: IRealtimeTransport;
  private readonly m_toolsModel: IAIAgentToolsModel;
  private readonly m_executor: AgentToolExecutorModel;
  private readonly m_policy: IRealtimeSessionPolicyProvider;
  /** Developer wire log (debug panel's Log tab). Optional — nothing depends on it. */
  private readonly m_log: IRealtimeConversationLog | null;

  /**
   * Whether the SERVER's session exists yet — set by the inbound
   * `session.created`, not by the transport connecting.
   *
   * ⚠ The two are not the same moment, and the gap silently ate the first
   * `session.update` of every session. Measured against the campfire realtime
   * proxy (2026-08-28): the client socket opens ~450 ms BEFORE `session.created`
   * arrives, because the proxy accepts the browser's connection and only then
   * dials upstream — and anything sent in that window is dropped with no
   * `session.updated` and no `error`. Sending after `session.created` was
   * acknowledged every time.
   *
   * So instructions, tool schemas, VAD and transcription config never landed at
   * connect. A TEXT turn self-healed (`sendUserText` refreshes first); a first
   * VOICE turn did not, and ran on server defaults with no tools.
   */
  private m_sessionReady = false;
  /** A `session.update` requested before the server session existed. */
  private m_pendingSessionUpdate = false;
  private m_readyFallbackTimer: number | null = null;

  private m_hasActiveResponse = false;
  private m_isThinking = false;
  private readonly m_pendingCalls = new Map<string, PendingFunctionCall>();
  private m_inFlightToolCount = 0;
  /**
   * At least one function output has been posted for the current tool round,
   * so the model needs exactly one continuation after the originating response
   * is done and every tool in that response has settled.
   */
  private m_toolFollowupPending = false;
  private m_suppressNextFollowup = false;
  private readonly m_offFns: Array<() => void> = [];
  /**
   * Post-stop manual Listen guard. The trace that drove this had server VAD
   * inventing "weighing" / "Here are" / "Its leaves are" from <1.1 s snippets
   * immediately after `output_audio_buffer.clear`.
   */
  private m_lastOutputInterruptedAtMs = 0;
  private m_guardNextManualListenInput = false;
  private readonly m_voiceInputsByItemId = new Map<string, PendingVoiceInput>();
  /**
   * The model this session's socket connected on, pinned at construction for
   * usage attribution: the policy's resolved model can move ahead of the
   * socket (a role switch mid-restart), and tokens must be priced against the
   * model that actually produced them.
   */
  private readonly m_model: string;

  constructor(
    transport: IRealtimeTransport,
    toolsModel: IAIAgentToolsModel,
    executor: AgentToolExecutorModel,
    policy: IRealtimeSessionPolicyProvider,
    log: IRealtimeConversationLog | null = null,
  ) {
    this.m_transport = transport;
    this.m_toolsModel = toolsModel;
    this.m_executor = executor;
    this.m_policy = policy;
    this.m_log = log;
    this.m_model = policy.resolveSessionConnection().model;

    this.m_offFns.push(
      transport.onConnected.on(() => {
        this.onStatusChanged.emit("connected");
        // Requests the update; `session.created` is what releases it.
        this.sendSessionUpdate();
        this.armReadyFallback();
      }),
      transport.onDisconnected.on(() => {
        this.clearReadyFallback();
        const wasReady = this.m_sessionReady;
        this.m_sessionReady = false;
        this.m_pendingSessionUpdate = false;
        if (wasReady) this.onReadyForUserInputChanged.emit(false);
        this.onStatusChanged.emit("disconnected");
      }),
      transport.onMessage.on((json) => this.handleRealtimeMessage(json)),
      transport.onError.on((code) => this.onError.emit(code)),
    );
    this.m_executor.setSessionId(this.sessionId);
  }

  /**
   * Whether an outgoing user item would actually reach the model.
   *
   * ⚠ STRICTER than "the transport is connected", and the difference is the
   * ~450 ms window {@link m_sessionReady} documents: a `conversation.item.create`
   * sent before `session.created` is dropped by the proxy with no error, and
   * the session config it would be answered under has not been flushed either
   * — so even a line that survived would be answered by a model with no
   * instructions (no `INTERNAL SAY:` contract, no tools).
   *
   * Read by callers that must not lose their message — a workflow script's
   * authored `say` line has no user behind it to notice the silence and retry.
   */
  get isReadyForUserInput(): boolean {
    return this.m_transport.isConnected && this.m_sessionReady;
  }

  get hasActiveResponse(): boolean {
    return this.m_hasActiveResponse;
  }

  get isThinking(): boolean {
    return this.m_isThinking;
  }

  shutdown(): void {
    this.clearReadyFallback();
    for (const off of this.m_offFns) off();
    this.m_offFns.length = 0;
    this.m_pendingCalls.clear();
  }

  /**
   * Rebuild instructions + tools (Unity `RefreshSessionContext`).
   *
   * Goes through the same readiness gate as the initial update: it fires on
   * every user turn and every role change, so an early role switch would race
   * into exactly the same hole.
   */
  refreshSessionContext(): void {
    if (!this.m_transport.isConnected) return;
    this.sendSessionUpdate();
  }

  suppressNextToolFollowupResponse(): void {
    this.m_suppressNextFollowup = true;
  }

  // -- Outgoing --------------------------------------------------------

  /**
   * `requestResponse: false` posts the item WITHOUT asking the model to answer
   * — used while the text bridge owns the turn, so the realtime conversation
   * keeps the user's words for context but only ever speaks `INTERNAL SAY:`
   * lines.
   */
  sendUserText(text: string, withTurnContext = true, requestResponse = true): void {
    if (!this.m_transport.isConnected) return;
    if (withTurnContext) this.trySendTurnContext();
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    });
    if (requestResponse) this.sendEvent({ type: "response.create" });
  }

  /**
   * Adds a line to the conversation for the model to READ — and nothing else:
   * no `response.create`, no turn context, no session refresh. The shape the
   * per-turn `User Context:` item already has, and for the same reasons: the
   * chat renders only what the user typed or said and what the model spoke, so
   * an item posted here never appears there; and it bypasses
   * `AIRealtimeSessionModel.sendUserText`, so it is not counted as a user turn
   * or recorded in the saved transcript.
   *
   * Best-effort: before the server session exists the proxy drops items
   * silently (see {@link m_sessionReady}), and a note is not worth queueing —
   * the next `session.update` carries the same state in its instructions.
   * Returns whether it was sent.
   */
  sendContextNote(text: string): boolean {
    if (!this.isReadyForUserInput) return false;
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text }],
      },
    });
    return true;
  }

  /**
   * `caption` rides the SAME conversation item as the image, as a second
   * content part — never a separate `sendUserText` beforehand.
   *
   * Two reasons it has to be one item. This method fires `response.create`
   * itself, so two calls are two responses, and the second one silently drops
   * (the socket takes one active response at a time). And a caption is the
   * request the picture is FOR: splitting them lets the model answer the image
   * before the question arrives.
   *
   * Absent — which is every caller but the panel's attachment path, the
   * `take_screenshot` executor included — the item is exactly what it was.
   */
  sendUserImage(dataUrl: string, caption?: string): boolean {
    if (!this.m_transport.isConnected) return false;
    const trimmed = caption?.trim() ?? "";
    const content: Array<Record<string, unknown>> = [
      { type: "input_image", image_url: dataUrl },
    ];
    if (trimmed) content.push({ type: "input_text", text: trimmed });
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content,
      },
    });
    this.sendEvent({ type: "response.create" });
    return true;
  }

  forceStopActiveResponse(): boolean {
    if (!this.m_transport.isConnected) return false;
    if (this.m_hasActiveResponse) {
      this.sendEvent({ type: "response.cancel" });
    }
    this.m_lastOutputInterruptedAtMs = Date.now();
    this.m_transport.stopOutputAudioPlayback();
    this.setActiveResponse(false);
    this.setThinking(false);
    return true;
  }

  notifyMicrophoneMuteChanged(
    muted: boolean,
    reason: RealtimeMicrophoneReason = "manual",
  ): void {
    if (muted || reason !== "push_to_talk") return;

    // Drop anything the server may still hold before the first frames of this
    // explicit Listen reach it. The command is harmless when the buffer is
    // already empty and keeps both transports aligned.
    this.sendEvent({ type: "input_audio_buffer.clear" });

    const sinceInterruptedOutputMs = Date.now() - this.m_lastOutputInterruptedAtMs;
    this.m_guardNextManualListenInput =
      sinceInterruptedOutputMs >= 0 &&
      sinceInterruptedOutputMs <= K_POST_OUTPUT_MANUAL_LISTEN_GUARD_MS;
    if (this.m_guardNextManualListenInput) {
      this.m_log?.note(
        "voice.guard.armed",
        `since_interrupted_output_ms=${sinceInterruptedOutputMs}`,
        { since_interrupted_output_ms: sinceInterruptedOutputMs },
      );
    }
  }

  private trySendTurnContext(): void {
    const userContext = this.m_policy.buildTurnUserContext();
    if (!userContext) return;
    this.sendEvent({
      type: "conversation.item.create",
      item: {
        type: "message",
        role: "user",
        content: [{ type: "input_text", text: `User Context:\n${userContext}` }],
      },
    });
  }

  /**
   * Sends the session config, or COALESCES it until the server session exists.
   *
   * Coalesced rather than queued: the payload is rebuilt from live state at
   * flush time, so several requests before readiness are one update carrying
   * the newest context — never a burst of ~30 KB updates.
   */
  private sendSessionUpdate(): void {
    if (!this.m_sessionReady) {
      this.m_pendingSessionUpdate = true;
      return;
    }
    this.sendEvent({ type: "session.update", session: this.buildSessionPayload() });
  }

  /**
   * Marks the server session live and flushes anything held for it.
   *
   * ⚠ Also reached by {@link armReadyFallback}, so a transport or proxy that
   * never forwards `session.created` degrades to the OLD behaviour rather than
   * leaving the session's context unset forever. Without that, this fix would
   * trade a probabilistic failure for a deterministic one.
   */
  private markSessionReady(): void {
    this.clearReadyFallback();
    if (this.m_sessionReady) return;
    this.m_sessionReady = true;
    if (this.m_pendingSessionUpdate) {
      this.m_pendingSessionUpdate = false;
      this.sendEvent({ type: "session.update", session: this.buildSessionPayload() });
    }
    // AFTER the config flush, never before: a waiter released here sends its
    // item on the next line, and the two ride one ordered connection — so the
    // instructions have to be on the wire first or the line is answered
    // without them.
    if (this.m_transport.isConnected) this.onReadyForUserInputChanged.emit(true);
  }

  private armReadyFallback(): void {
    this.clearReadyFallback();
    this.m_readyFallbackTimer = window.setTimeout(() => {
      this.m_readyFallbackTimer = null;
      if (this.m_sessionReady) return;
      console.warn(
        "[RealtimeSession] no session.created within " +
          `${K_SESSION_READY_FALLBACK_MS}ms — sending the session config anyway`,
      );
      this.m_log?.note("session.ready-fallback", `${K_SESSION_READY_FALLBACK_MS}ms`);
      this.markSessionReady();
    }, K_SESSION_READY_FALLBACK_MS);
  }

  private clearReadyFallback(): void {
    if (this.m_readyFallbackTimer === null) return;
    window.clearTimeout(this.m_readyFallbackTimer);
    this.m_readyFallbackTimer = null;
  }

  /**
   * The `session.update` body as it would be sent RIGHT NOW — instructions,
   * the enabled tool schemas for the active role/skills, and the audio config.
   *
   * Public and side-effect-free so the debug panel's "Context" tab can
   * render the live payload rather than a stale copy captured at connect time:
   * the instructions carry the project/scene context and the tool list is
   * role-gated, so both change while the session is up. The connected session
   * is not consulted at all, which is also why this answers before a session
   * exists.
   */
  buildSessionPayload(): Record<string, unknown> {
    return RealtimeSession.buildSessionPayloadFrom(this.m_policy, this.m_toolsModel);
  }

  /**
   * The same payload without an instance. `AIRealtimeSessionModel` uses it to
   * answer the debug panel while Spark is offline, when there is no session to
   * ask — everything the body is made of comes from the policy and the tools
   * model, neither of which needs a connection.
   */
  static buildSessionPayloadFrom(
    policy: IRealtimeSessionPolicyProvider,
    toolsModel: IAIAgentToolsModel,
  ): Record<string, unknown> {
    const options = policy.buildSessionOptions();
    const config = getAiConfig();
    const modelConfig = policy.resolveSessionConnection();
    const session: Record<string, unknown> = {
      type: "realtime",
      instructions: options.instructions,
      // Only present when the active role configures one (`roleModels`):
      // the stock realtime path keeps its wire shape byte-identical.
      ...(modelConfig.reasoningEffort !== null
        ? { reasoning: { effort: modelConfig.reasoningEffort } }
        : {}),
      output_modalities: options.enableAudioOutput ? ["audio"] : ["text"],
      tools: toolsModel.getEnabledToolSchemas(),
      audio: {
        input: {
          format: { type: "audio/pcm", rate: 24000 },
          // Webcams and laptop microphones are far-field sources. Server-side
          // filtering runs before VAD/transcription and supplements the
          // browser's getUserMedia noise suppression.
          noise_reduction: { type: "far_field" },
          turn_detection: {
            type: "server_vad",
            create_response: options.autoRespondToUserAudio,
            interrupt_response: true,
            threshold: SERVER_VAD.threshold,
            prefix_padding_ms: SERVER_VAD.prefix_padding_ms,
            silence_duration_ms: SERVER_VAD.silence_duration_ms,
            idle_timeout_ms: null,
          },
          // The language pin is OMITTED when empty rather than sent blank: an
          // unknown or malformed key here is refused at `session.update`, which
          // voids the WHOLE update so instructions and tool schemas never land
          // either. That is also what the orchestrator's transcription
          // self-heal clears it for.
          //
          // ⚠ NOTHING PROJECT-DERIVED MAY BE ADDED HERE. A `prompt` carrying the
          // scene's proper nouns shipped 2026-09-06 and was reverted the same
          // day: on a near-silent buffer `gpt-4o-transcribe` CONTINUED it and
          // returned the continuation as a completed user transcript, which
          // renders as the user speaking, wipes the agent's on-screen
          // measurement, writes to the durable transcript and can release a
          // workflow `wait_for`. `prompt` is documented for describing the
          // recording's setting, not for vocabulary; the purpose-built field is
          // `keywords` ("Keywords are hints, not required output"), which this
          // endpoint's model does not take. Measure that on a throwaway session
          // before reaching for it — see the ⚠ on the self-heal latch.
          ...(options.enableInputAudioTranscription
            ? {
                transcription: {
                  model: config.transcriptionModel,
                  ...(options.transcriptionLanguage
                    ? { language: options.transcriptionLanguage }
                    : {}),
                },
              }
            : {}),
        },
        output: {
          format: { type: "audio/pcm", rate: 24000 },
          voice: options.voiceName,
        },
      },
    };
    return session;
  }

  private sendEvent(event: Record<string, unknown>): void {
    if (import.meta.env.DEV) {
      console.debug(`[RealtimeSession] → ${String(event.type)}`);
    }
    this.m_log?.logOutbound(event);
    this.m_transport.sendEvent(JSON.stringify(event));
  }

  // -- Incoming --------------------------------------------------------

  private handleRealtimeMessage(json: string): void {
    let event: Record<string, unknown>;
    try {
      event = JSON.parse(json) as Record<string, unknown>;
    } catch {
      return;
    }
    const type = typeof event.type === "string" ? event.type : "";
    this.m_log?.logInbound(event);

    switch (type) {
      case "session.created":
        // The moment the server session exists — see `m_sessionReady`.
        this.markSessionReady();
        break;
      case "session.updated":
        break;
      case "response.created":
        this.setActiveResponse(true);
        this.setThinking(true);
        // Restores Unity's `IRealtimeTransport` contract ("called when session
        // detects new audio output starting"), which the web port had dropped.
        // On the WebSocket transport this is a harmless `context.resume()`; on
        // WebRTC it is the DETERMINISTIC un-silence after a barge-in, without
        // which one Stop press deafens Spark for the rest of the session.
        this.m_transport.resumeAudioOutputIfNeeded();
        break;
      case "response.output_text.delta":
      case "response.output_audio_transcript.delta": {
        // Guarded for the same reason as `response.done`: a model that speaks
        // before it calls a tool would otherwise clear the flag for the round.
        this.clearThinkingUnlessToolsInFlight();
        const delta = typeof event.delta === "string" ? event.delta : "";
        if (delta) this.onResponseDelta.emit(delta);
        break;
      }
      case "response.output_text.done":
      case "response.output_audio_transcript.done": {
        const text = typeof event.text === "string" ? event.text : (event.transcript as string) ?? "";
        if (text) this.onAssistantTextCompleted.emit(text);
        break;
      }
      case "response.done":
        this.setActiveResponse(false);
        // ⚠ NOT an unconditional clear: a response carrying a function call is
        // DONE the moment it has emitted the call, which is long before the
        // tool it asked for has run. See `clearThinkingUnlessToolsInFlight`.
        this.clearThinkingUnlessToolsInFlight();
        this.parseResponseUsage(event);
        this.trySendToolFollowup();
        break;
      case "response.output_item.added":
        this.handleOutputItemAdded(event);
        break;
      case "response.function_call_arguments.done":
        this.handleFunctionArgsDone(event);
        break;
      case "input_audio_buffer.speech_started":
        this.recordInputSpeechStarted(event);
        this.onUserVoiceStarted.emit();
        this.trySendTurnContext();
        break;
      case "input_audio_buffer.speech_stopped":
        this.recordInputSpeechStopped(event);
        this.onUserVoiceStopped.emit();
        break;
      case "conversation.item.input_audio_transcription.delta": {
        const delta = typeof event.delta === "string" ? event.delta : "";
        if (delta) this.onUserTranscriptDelta.emit(delta);
        break;
      }
      case "conversation.item.input_audio_transcription.completed": {
        const transcript = typeof event.transcript === "string" ? event.transcript : "";
        if (this.shouldSuppressTranscript(event, transcript)) {
          this.parseTranscriptionUsage(event);
          break;
        }
        if (transcript) this.onUserTranscriptCompleted.emit(transcript);
        this.parseTranscriptionUsage(event);
        break;
      }
      case "error": {
        const error = event.error as Record<string, unknown> | undefined;
        const code = typeof error?.code === "string" ? error.code : "realtime_error";
        if (import.meta.env.DEV) {
          console.warn("[RealtimeSession] server error event:", JSON.stringify(error ?? event));
        }
        // Benign races Unity suppresses too.
        if (code !== "response_cancel_not_active" && code !== "conversation_already_has_active_response") {
          this.onError.emit(code);
        }
        break;
      }
      default:
        break;
    }
  }

  private recordInputSpeechStarted(event: Record<string, unknown>): void {
    const itemId = typeof event.item_id === "string" ? event.item_id : "";
    if (!itemId) {
      this.m_guardNextManualListenInput = false;
      return;
    }
    const startAudioMs = typeof event.audio_start_ms === "number" ? event.audio_start_ms : null;
    this.m_voiceInputsByItemId.set(itemId, {
      guarded: this.m_guardNextManualListenInput,
      startAudioMs,
      endAudioMs: null,
    });
    this.m_guardNextManualListenInput = false;
  }

  private recordInputSpeechStopped(event: Record<string, unknown>): void {
    const itemId = typeof event.item_id === "string" ? event.item_id : "";
    if (!itemId) return;
    const input = this.m_voiceInputsByItemId.get(itemId);
    if (!input) return;
    input.endAudioMs = typeof event.audio_end_ms === "number" ? event.audio_end_ms : null;
  }

  private shouldSuppressTranscript(
    event: Record<string, unknown>,
    transcript: string,
  ): boolean {
    const itemId = typeof event.item_id === "string" ? event.item_id : "";
    if (!itemId) return false;
    const input = this.m_voiceInputsByItemId.get(itemId);
    this.m_voiceInputsByItemId.delete(itemId);
    if (!input?.guarded) return false;
    if (input.startAudioMs === null || input.endAudioMs === null) return false;

    const durationMs = input.endAudioMs - input.startAudioMs;
    if (durationMs < 0 || durationMs > K_GUARDED_INPUT_ARTIFACT_MAX_MS) return false;

    this.m_log?.note(
      "voice.guard.suppressed",
      `duration_ms=${durationMs}, transcript=${JSON.stringify(transcript)}`,
      { duration_ms: durationMs, transcript },
    );
    this.cancelSuppressedInputResponse();
    this.sendEvent({ type: "conversation.item.delete", item_id: itemId });
    return true;
  }

  private cancelSuppressedInputResponse(): void {
    if (this.m_hasActiveResponse) {
      this.sendEvent({ type: "response.cancel" });
    }
    this.m_transport.stopOutputAudioPlayback();
    this.setActiveResponse(false);
    this.setThinking(false);
  }

  private handleOutputItemAdded(event: Record<string, unknown>): void {
    const item = event.item as Record<string, unknown> | undefined;
    if (!item || item.type !== "function_call") return;
    const callId = typeof item.call_id === "string" ? item.call_id : "";
    const name = typeof item.name === "string" ? item.name : "";
    if (!callId || !name) return;
    this.m_pendingCalls.set(callId, {
      callId,
      name,
      argsJson: typeof item.arguments === "string" ? item.arguments : "",
    });
    // Inline args (rare) execute immediately; streamed args wait for done.
    if (this.m_pendingCalls.get(callId)!.argsJson) {
      void this.executePendingCall(callId);
    }
  }

  private handleFunctionArgsDone(event: Record<string, unknown>): void {
    const callId = typeof event.call_id === "string" ? event.call_id : "";
    if (!callId) return;
    const argsJson = typeof event.arguments === "string" ? event.arguments : "";
    const pending = this.m_pendingCalls.get(callId);
    if (pending) {
      pending.argsJson = argsJson;
    } else {
      const name = typeof event.name === "string" ? event.name : "";
      if (!name) return;
      this.m_pendingCalls.set(callId, { callId, name, argsJson });
    }
    void this.executePendingCall(callId);
  }

  private async executePendingCall(callId: string): Promise<void> {
    const pending = this.m_pendingCalls.get(callId);
    if (!pending) return;
    this.m_pendingCalls.delete(callId);

    let args: Record<string, unknown> = {};
    if (pending.argsJson) {
      try {
        args = JSON.parse(pending.argsJson) as Record<string, unknown>;
      } catch {
        args = {};
      }
    }

    this.m_inFlightToolCount++;
    this.setThinking(true);
    this.onToolExecutionStarted.emit({ toolName: pending.name });
    // Logged with the resolved ARGS, not the raw fragment stream: the debug
    // Log tab wants what the tool was actually called with.
    this.m_log?.logTool(pending.name, "call", args);

    let result: string;
    try {
      result = await this.m_executor.executeAsync(pending.name, args, "AI");
    } catch (err) {
      result = `error: ${err instanceof Error ? err.message : String(err)}`;
    }
    this.m_inFlightToolCount--;
    this.onToolExecutionCompleted.emit({ toolName: pending.name, result });
    this.m_log?.logTool(pending.name, "result", result);

    // Some tools hand the SPOKEN turn away — the learner answers a
    // `present_options` round with a button, and a workflow step the run just
    // moved to narrates itself. Asking for a follow-up reply there either talks
    // over whoever is speaking or, for a step arrival, gets composed from the
    // NEW step's freshly-pushed run-state bullets. `toolTurnHandover.ts` owns
    // the rule; the Responses bridge reads the same one.
    if (toolHandedOverSpokenTurn(pending.name, result)) {
      this.m_suppressNextFollowup = true;
    }

    this.sendEvent({
      type: "conversation.item.create",
      item: { type: "function_call_output", call_id: callId, output: result },
    });
    this.m_toolFollowupPending = true;
    this.trySendToolFollowup();
  }

  /**
   * Continue a tool round only after BOTH boundaries have closed:
   *
   * 1. every tool emitted by the response has finished; and
   * 2. the response itself has emitted `response.done`.
   *
   * The second boundary is load-bearing for parallel calls. A fast first tool
   * can finish before the server has even announced the response's second
   * function call. Continuing at that instant starts an answer over the slow
   * tool and makes its eventual `response.create` fail with
   * `conversation_already_has_active_response`.
   */
  private trySendToolFollowup(): void {
    if (
      !this.m_toolFollowupPending
      || this.m_hasActiveResponse
      || this.m_inFlightToolCount > 0
      || this.m_pendingCalls.size > 0
    ) {
      return;
    }

    this.m_toolFollowupPending = false;
    if (this.m_suppressNextFollowup) {
      this.m_suppressNextFollowup = false;
      this.setThinking(false);
    } else {
      this.sendEvent({ type: "response.create" });
    }
  }

  /**
   * End the thinking state ONLY if no tool is still running.
   *
   * ⚠ A response that carries a function call reaches `response.done` as soon
   * as it has emitted that call — the model's turn is over, but the WORK it
   * asked for has not started. So an unconditional clear there switches the
   * activity visualizer off for the whole tool round, and the slower the tool
   * the worse it is: a fast tool re-arms the flag within a frame or two when
   * the follow-up `response.created` lands, which is why this was invisible
   * until `Prompt_Search_Docs` — a gpt-5.6 Responses call that takes SECONDS —
   * left the pill dark for the entire search (user-reported, 2026-09-04).
   *
   * This does NOT risk a stuck flag. `m_inFlightToolCount` is decremented in
   * the dispatch's own tail (after a try/catch, so a throwing tool still
   * counts down), and that tail then either sends the follow-up `response.create`
   * — whose eventual `response.done` clears with the count back at 0 — or, for
   * a handover, clears explicitly. `forceStopActiveResponse` deliberately keeps
   * its UNCONDITIONAL `setThinking(false)`: the user pressed Stop, so
   * everything stops whatever is in flight.
   */
  private clearThinkingUnlessToolsInFlight(): void {
    if (this.m_inFlightToolCount > 0) return;
    this.setThinking(false);
  }

  private setThinking(thinking: boolean): void {
    if (this.m_isThinking === thinking) return;
    this.m_isThinking = thinking;
    this.onThinkingStateChanged.emit(thinking);
  }

  private setActiveResponse(active: boolean): void {
    if (this.m_hasActiveResponse === active) return;
    this.m_hasActiveResponse = active;
    this.onActiveResponseChanged.emit(active);
  }

  private parseResponseUsage(event: Record<string, unknown>): void {
    const response = event.response as Record<string, unknown> | undefined;
    const usage = response?.usage as Record<string, unknown> | undefined;
    if (!usage) return;
    const details = (usage.input_token_details ?? {}) as Record<string, unknown>;
    const outDetails = (usage.output_token_details ?? {}) as Record<string, unknown>;
    const cached = (details.cached_tokens_details ?? {}) as Record<string, unknown>;
    this.onUsage.emit({
      model: this.m_model,
      inputTokens: num(details.text_tokens) || num(usage.input_tokens),
      outputTokens: num(outDetails.text_tokens) || num(usage.output_tokens),
      inputAudioTokens: num(details.audio_tokens),
      outputAudioTokens: num(outDetails.audio_tokens),
      cachedInputTokens: num(details.cached_tokens) || num(cached.text_tokens),
      source: "Realtime",
    });
  }

  private parseTranscriptionUsage(event: Record<string, unknown>): void {
    const usage = event.usage as Record<string, unknown> | undefined;
    if (!usage) return;
    const details = (usage.input_token_details ?? {}) as Record<string, unknown>;
    this.onUsage.emit({
      model: getAiConfig().transcriptionModel,
      inputTokens: num(usage.input_tokens),
      outputTokens: num(usage.output_tokens),
      inputAudioTokens: num(details.audio_tokens),
      outputAudioTokens: 0,
      cachedInputTokens: 0,
      source: "Input Transcription",
    });
  }
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
